/**
 * Navigation configuration types for community customization
 * Based on best practices from leading no-code platforms
 */

// import { LucideIcon } from 'lucide-react';

export interface NavigationItem {
  id: string;
  name: string;
  href: string;
  icon: string; // Name of the Lucide icon
  isActive?: boolean;
  isEnabled?: boolean;
  order: number;
  description?: string;
  isCustom?: boolean; // True if user-created, false if predefined
}

export interface LogoClickSettings {
  destination: string; // URL or path where logo click should go
  type: 'internal' | 'external'; // Internal route or external URL
  openInNewTab?: boolean; // For external links
}

export interface NavigationSettings {
  items: NavigationItem[];
  style: NavigationStyle;
  logoClick: LogoClickSettings;
  enableDropdowns?: boolean;
  enableIcons?: boolean;
  maxVisibleItems?: number;
}

export interface NavigationStyle {
  layout: 'horizontal' | 'pills' | 'underline';
  alignment: 'left' | 'center' | 'right';
  spacing: 'compact' | 'comfortable' | 'spacious';
  showIcons: boolean;
  iconPosition: 'left' | 'right' | 'top';
}

export interface PredefinedNavigationItem {
  id: string;
  name: string;
  href: string;
  icon: string;
  description: string;
  category: 'essential' | 'community' | 'content' | 'engagement';
  isDefault?: boolean;
}

// Predefined navigation items that communities can choose from
export const PREDEFINED_NAVIGATION_ITEMS: PredefinedNavigationItem[] = [
  // Essential
  {
    id: 'home',
    name: 'Home',
    href: '/',
    icon: 'Home',
    description: 'Community homepage',
    category: 'essential',
    isDefault: false, // Not default anymore - can be added manually
  },
  {
    id: 'jobs',
    name: 'Jobs',
    href: '/jobs',
    icon: 'Briefcase',
    description: 'Job listings and opportunities',
    category: 'essential',
    isDefault: false,
  },
  {
    id: 'events',
    name: 'Events',
    href: '/events',
    icon: 'Calendar',
    description: 'Community events and meetups',
    category: 'community',
    isDefault: false,
  },

  // Community
  {
    id: 'directory',
    name: 'Directory',
    href: '/directory',
    icon: 'Users',
    description: 'Member directory',
    category: 'community',
  },
  {
    id: 'groups',
    name: 'Groups',
    href: '/groups',
    icon: 'Users2',
    description: 'Special interest groups',
    category: 'community',
  },
  {
    id: 'mentorship',
    name: 'Mentorship',
    href: '/mentorship',
    icon: 'GraduationCap',
    description: 'Mentorship programs',
    category: 'community',
  },

  // Content
  {
    id: 'blog',
    name: 'Blog',
    href: '/blog',
    icon: 'FileText',
    description: 'Community blog and articles',
    category: 'content',
  },
  {
    id: 'resources',
    name: 'Resources',
    href: '/resources',
    icon: 'BookOpen',
    description: 'Helpful resources and guides',
    category: 'content',
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    href: '/newsletter',
    icon: 'Mail',
    description: 'Community newsletter',
    category: 'content',
  },

  // Engagement
  {
    id: 'channels',
    name: 'Channels',
    href: '/channels',
    icon: 'MessageSquare',
    description: 'Community channels and messaging',
    category: 'engagement',
  },
  {
    id: 'discussions',
    name: 'Discussions',
    href: '/discussions',
    icon: 'MessageCircle',
    description: 'Community discussions',
    category: 'engagement',
  },
  {
    id: 'q-and-a',
    name: 'Q&A',
    href: '/qa',
    icon: 'HelpCircle',
    description: 'Questions and answers',
    category: 'engagement',
  },
  {
    id: 'polls',
    name: 'Polls',
    href: '/polls',
    icon: 'BarChart3',
    description: 'Community polls',
    category: 'engagement',
  },
];

export const DEFAULT_NAVIGATION_ITEMS: NavigationItem[] =
  PREDEFINED_NAVIGATION_ITEMS.filter((item) => item.isDefault).map(
    (item, index) => ({
      id: item.id,
      name: item.name,
      href: item.href,
      icon: item.icon,
      isActive: true,
      isEnabled: true,
      order: index,
      description: item.description,
      isCustom: false,
    })
  );

export const DEFAULT_NAVIGATION_STYLE: NavigationStyle = {
  layout: 'underline',
  alignment: 'center',
  spacing: 'comfortable',
  showIcons: false,
  iconPosition: 'left',
};

export const DEFAULT_LOGO_CLICK_SETTINGS: LogoClickSettings = {
  destination: '/',
  type: 'internal',
  openInNewTab: false,
};

export const DEFAULT_NAVIGATION_SETTINGS: NavigationSettings = {
  items: DEFAULT_NAVIGATION_ITEMS,
  style: DEFAULT_NAVIGATION_STYLE,
  logoClick: DEFAULT_LOGO_CLICK_SETTINGS,
  enableDropdowns: false,
  enableIcons: false,
  maxVisibleItems: 6,
};

// Available Lucide icons for navigation (curated list)
export const NAVIGATION_ICONS = [
  'Home',
  'Briefcase',
  'Calendar',
  'BookOpen',
  'User',
  'Users',
  'Building2',
  'Globe',
  'ExternalLink',
  'Mail',
  'Phone',
  'MapPin',
  'Star',
  'Heart',
  'Zap',
  'Target',
  'Award',
  'Coffee',
  'Laptop',
  'Megaphone',
  'Newspaper',
  'PieChart',
  'Shield',
  'Settings',
  'HelpCircle',
  'MessageSquare',
  'MessageCircle',
  'BarChart3',
] as const;

export type NavigationIconName = (typeof NAVIGATION_ICONS)[number];

// Navigation template for quick setup
export interface NavigationTemplate {
  id: string;
  name: string;
  description: string;
  logoDestination: string;
  items: {
    label: string;
    icon: string;
    destination: string;
    type: 'internal' | 'external';
    enabled: boolean;
    order: number;
    openInNewTab?: boolean;
  }[];
}

// Default templates based on common use cases
export const NAVIGATION_TEMPLATES: NavigationTemplate[] = [
  {
    id: 'jobs-focused',
    name: 'Job Board Focus',
    description: 'Perfect for employment-focused communities',
    logoDestination: '/jobs',
    items: [
      {
        label: 'Find Jobs',
        icon: 'Briefcase',
        destination: '/jobs',
        type: 'internal',
        enabled: true,
        order: 1,
      },
      {
        label: 'Events',
        icon: 'Calendar',
        destination: '/events',
        type: 'internal',
        enabled: true,
        order: 2,
      },
      {
        label: 'Resources',
        icon: 'BookOpen',
        destination: '/academy',
        type: 'internal',
        enabled: true,
        order: 3,
      },
    ],
  },
  {
    id: 'community-hub',
    name: 'Community Hub',
    description: 'Balanced navigation for general communities',
    logoDestination: '/',
    items: [
      {
        label: 'Home',
        icon: 'Home',
        destination: '/',
        type: 'internal',
        enabled: true,
        order: 1,
      },
      {
        label: 'Jobs',
        icon: 'Briefcase',
        destination: '/jobs',
        type: 'internal',
        enabled: true,
        order: 2,
      },
      {
        label: 'Events',
        icon: 'Calendar',
        destination: '/events',
        type: 'internal',
        enabled: true,
        order: 3,
      },
      {
        label: 'Members',
        icon: 'Users',
        destination: '/members',
        type: 'internal',
        enabled: true,
        order: 4,
      },
    ],
  },
  {
    id: 'external-focused',
    name: 'External Links',
    description: 'Drive traffic to your main website and resources',
    logoDestination: 'https://example.com',
    items: [
      {
        label: 'Our Website',
        icon: 'Globe',
        destination: 'https://example.com',
        type: 'external',
        enabled: true,
        order: 1,
        openInNewTab: true,
      },
      {
        label: 'Find Jobs',
        icon: 'Briefcase',
        destination: '/jobs',
        type: 'internal',
        enabled: true,
        order: 2,
      },
      {
        label: 'Contact',
        icon: 'Mail',
        destination: 'https://example.com/contact',
        type: 'external',
        enabled: true,
        order: 3,
        openInNewTab: true,
      },
    ],
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Clean and simple navigation',
    logoDestination: '/jobs',
    items: [
      {
        label: 'Jobs',
        icon: 'Briefcase',
        destination: '/jobs',
        type: 'internal',
        enabled: true,
        order: 1,
      },
      {
        label: 'About',
        icon: 'Globe',
        destination: 'https://example.com/about',
        type: 'external',
        enabled: true,
        order: 2,
        openInNewTab: true,
      },
    ],
  },
];
